import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Download, ChevronDown } from "lucide-react";
import api from "../lib/api";

// One shared Export menu for every admin student list.
// CSV/PDF are fetched with the Authorization header and saved as a blob — never a token in a URL.
// Excel (.xlsx) is omitted: openpyxl is not installed on the backend.
export const ExportMenu = ({ list, params = {}, title, columns = [], rows = [] }) => {
  const [open, setOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const download = async (format) => {
    setOpen(false);
    try {
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      );
      const qs = new URLSearchParams({ ...clean, format }).toString();
      const r = await api.get(`/admin/exports/${list}?${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `workingactor-${list}-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed — please try again.");
    }
  };

  const print = () => {
    setOpen(false);
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 200);
  };

  const stamp =
    new Date().toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    }) + " PT";

  return (
    <div className="relative no-print" ref={ref} data-testid="export-menu">
      <button onClick={() => setOpen((o) => !o)} className="btn-outline text-sm" data-testid="export-menu-button">
        <Download size={14} /> Export <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-hairline shadow-lg min-w-[180px] p-1">
          <button onClick={() => download("csv")} className="w-full text-left px-3 py-2 font-sans text-sm hover:bg-paper" data-testid="export-csv">
            Spreadsheet (.csv)
          </button>
          <button onClick={() => download("pdf")} className="w-full text-left px-3 py-2 font-sans text-sm hover:bg-paper" data-testid="export-pdf">
            PDF
          </button>
          <button onClick={print} className="w-full text-left px-3 py-2 font-sans text-sm hover:bg-paper" data-testid="export-print">
            Print
          </button>
        </div>
      )}
      {printing &&
        createPortal(
          <div className="print-surface" data-testid="print-surface">
            <h1>Working Actor · {title || list}</h1>
            <p className="print-meta">Printed {stamp} · {rows.length} row{rows.length === 1 ? "" : "s"}</p>
            <table>
              <thead>
                <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i}>
                    {columns.map((c) => <td key={c.key}>{c.render ? c.render(r) : String(r[c.key] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
          document.body
        )}
    </div>
  );
};
